<template>
  <span class="cam-icon" :class="['cam-' + type, 'cam-' + state]" :style="wrapStyle">
    <!-- 平面图上的枪机：用俯视 SVG，旋转更自然，并带视野扇形 -->
    <svg
      v-if="type === 'bullet' && showFov"
      class="cam-svg"
      :width="size"
      :height="size"
      viewBox="-34 -34 68 68"
    >
      <g :transform="`rotate(${directionNum})`">
        <!-- 视野扇形：柔和渐变，表示监控范围 -->
        <path
          class="cam-fov"
          :fill="fovFill"
          d="M4,0 L36,-17 A38,38 0 0,1 36,17 Z"
        />
        <!-- 枪机主体（俯视）：机身 + 镜头 -->
        <rect x="-16" y="-6" width="20" height="12" rx="3" :fill="color" />
        <rect x="2" y="-4" width="7" height="8" rx="2" :fill="color" />
        <circle cx="6" cy="0" r="2.5" fill="#fff" opacity="0.85" />
      </g>
    </svg>
    <!-- 列表/球机/不需要朝向的枪机：沿用原 mask 图标 -->
    <span v-else class="cam-body" :style="bodyStyle"></span>
  </span>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  // bullet = 枪机, dome = 球机
  type: { type: String, default: 'dome' },
  // 1 = 在线，其它 = 离线
  status: { type: [Number, String], default: 1 },
  // 新增未保存的临时摄像机
  pending: { type: Boolean, default: false },
  // 图标尺寸(px)，默认 30
  size: { type: [Number, String], default: 30 },
  // 朝向角度：0 向右，90 向下，180 向左，270 向上（仅枪机生效）
  direction: { type: [Number, String], default: 0 },
  // 是否显示枪机视野扇形（平面图上使用，列表上一般关闭）
  showFov: { type: Boolean, default: false }
})

const state = computed(() => {
  if (props.pending) return 'pending'
  return props.status == 1 ? 'online' : 'offline'
})

const directionNum = computed(() => Number(props.direction || 0))

// 用户提供的图标（public/assets/images 下，中文名做 URL 编码，避免 css-loader 解析）
// 用 SVG 作为 CSS mask，再按在线/离线状态给 background-color 染色
const ICON_BULLET = "/assets/images/%E6%9E%AA%E6%9C%BA.svg" // 枪机.svg
const ICON_DOME   = "/assets/images/%E7%90%83%E6%9C%BA.svg" // 球机.svg

const COLORS = { online: '#67c23a', offline: '#909399', pending: '#e6a23c' }
const color = computed(() => COLORS[state.value])

const wrapStyle = computed(() => ({
  width: props.size + 'px',
  height: props.size + 'px'
}))

const bodyStyle = computed(() => {
  const url = props.type === 'bullet' ? ICON_BULLET : ICON_DOME
  return {
    backgroundColor: color.value,
    WebkitMaskImage: `url("${url}")`,
    maskImage: `url("${url}")`
  }
})

const fovFill = computed(() => hexToRgba(color.value, 0.4))

function hexToRgba(hex, alpha) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!m) return `rgba(103,194,58,${alpha})`
  const r = parseInt(m[1], 16)
  const g = parseInt(m[2], 16)
  const b = parseInt(m[3], 16)
  return `rgba(${r},${g},${b},${alpha})`
}
</script>

<style scoped>
.cam-icon {
  position: relative;
  display: inline-block;
  overflow: visible;
  vertical-align: middle;
}
.cam-body {
  display: block;
  width: 100%;
  height: 100%;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  transition: background-color .2s;
}
.cam-svg {
  display: block;
  overflow: visible;
}
.cam-svg .cam-fov {
  opacity: .65;
  pointer-events: none;
  transition: fill .2s;
}
</style>
